import {
  Badge,
  Button,
  Card,
  Combobox,
  SelectOption,
  SelectSizeVariant,
} from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState, type JSX } from 'react';

import useAddTeamMember from '@/api/mutations/teams/useAddTeamMember';
import useRemoveTeamMember from '@/api/mutations/teams/useRemoveTeamMember';
import useGetMyAccountTeams from '@/api/queries/me/useGetMyAccountTeams';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';

type Team = { id: string; name: string };

type Props = {
  /** Public ID of the user whose teams are being edited. */
  userPublicId: string;
  /** The teams the user currently belongs to. */
  teams: Team[];
  /** Called after a membership change so the caller can refetch the user. */
  onChanged: () => void;
};

const UserTeamsCard: FC<Props> = ({
  userPublicId,
  teams,
  onChanged,
}): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);

  const { data: accountTeams } = useGetMyAccountTeams();
  const addMember = useAddTeamMember({ onSuccess: onChanged });
  const removeMember = useRemoveTeamMember({ onSuccess: onChanged });

  const assignedTeamIds = new Set(teams.map((team) => team.id));
  const addableTeamOptions: SelectOption<string>[] = (accountTeams ?? [])
    .filter((team) => !assignedTeamIds.has(team.id))
    .map((team) => ({ label: team.name, value: team.id }));

  return (
    <Card>
      <Card.Body>
        <div className="flex items-center justify-between">
          <h3>Teams</h3>
          <PermissionGuard
            renderIfHasPermissions={[AccountsPermissions.MANAGE_TEAM_MEMBERS]}
          >
            <Button
              size="sm"
              onClick={() => setIsEditing((editing) => !editing)}
              variant={isEditing ? 'primary' : 'secondary'}
            >
              {isEditing ? 'Done' : 'Edit'}
            </Button>
          </PermissionGuard>
        </div>

        {teams.length === 0 ? (
          !isEditing && (
            <p className="mt-1 text-gray-500">Not in any teams yet</p>
          )
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {teams.map((team) => {
              const isRemoving =
                removeMember.isPending &&
                removeMember.variables?.teamId === team.id;

              return (
                <Badge key={team.id} variant="soft">
                  {team.name}
                  {isEditing && (
                    <button
                      type="button"
                      aria-label={`Remove from ${team.name}`}
                      disabled={isRemoving}
                      className="ml-1 inline-flex cursor-pointer appearance-none items-center border-0 bg-transparent p-0 text-gray-500 hover:text-gray-800 disabled:opacity-50"
                      onClick={() =>
                        removeMember.mutate({
                          teamId: team.id,
                          userId: userPublicId,
                        })
                      }
                    >
                      <UntitledIcon name="x-close" className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
        )}

        {isEditing && (
          <div className="mt-3">
            {addableTeamOptions.length > 0 && (
              <Combobox<string, false>
                multiple={false}
                value={undefined}
                options={addableTeamOptions}
                sizeVariant={SelectSizeVariant.SM}
                hideDropdownChevron
                inputPlaceholder="Add to team"
                customButtonContent={
                  <span className="flex items-center gap-2 text-gray-500">
                    <UntitledIcon name="plus.3" className="h-4 w-4" />
                    Add to team
                  </span>
                }
                onChange={(teamId) => {
                  if (teamId) {
                    addMember.mutate({ teamId, userId: userPublicId });
                  }
                }}
              />
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default UserTeamsCard;
